import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../../services/api";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const STATUS_CONFIG = {
  payee:      { label: "Payée",      color: "#1a9e72", bg: "#1a9e7222" },
  impayee:    { label: "Impayée",    color: "#e24b4a", bg: "#e24b4a22" },
  en_attente: { label: "En attente", color: "#ba7517", bg: "#ba751722" },
};

export default function BillingTab({ currentCompanyId }) {
  const [history, setHistory]  = useState([]);
  const [loading, setLoading]  = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.subscriptions.billingHistory(currentCompanyId)
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  // Group by month for chart
  const byMonth = history.reduce((acc, inv) => {
    const m = inv.billing_date ? inv.billing_date.slice(0, 7) : "?";
    if (!acc[m]) acc[m] = 0;
    acc[m] += inv.amount_ttc || 0;
    return acc;
  }, {});

  const chartData = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([month, total]) => ({
      month: month.slice(5) + "/" + month.slice(2, 4),
      total: Math.round(total),
    }));

  const filtered = history.filter(inv =>
    filterStatus === "all" || inv.status === filterStatus
  );

  const totalRevenue = filtered.reduce((s, inv) => s + (inv.amount_ttc || 0), 0);
  const paidRevenue  = history.filter(i => i.status === "payee").reduce((s, i) => s + (i.amount_ttc || 0), 0);

  function exportCSV() {
    const headers = ["Date", "Client", "Abonnement", "Fréquence", "Facture", "Montant TTC", "Statut"];
    const rows = filtered.map(inv => [
      fmtDate(inv.billing_date),
      inv.customer_name,
      inv.subscription_name,
      inv.frequency,
      inv.invoice_number || "—",
      fmt(inv.amount_ttc),
      STATUS_CONFIG[inv.status]?.label || inv.status,
    ]);
    const csv = [headers, ...rows].map(r => r.join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `facturation_abonnements_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      {/* KPIs */}
      <div className="kpi-grid k3" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <div className="kpi-label">🧾 Factures générées</div>
          <div className="kpi-value">{history.length}</div>
          <div className="kpi-sub">Total abonnements</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">✅ Revenus encaissés</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(paidRevenue)} €</div>
          <div className="kpi-sub">{history.filter(i => i.status === "payee").length} factures payées</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">⏳ En attente</div>
          <div className="kpi-value" style={{ color: "var(--amber)" }}>
            {fmt(history.filter(i => i.status !== "payee").reduce((s, i) => s + (i.amount_ttc || 0), 0))} €
          </div>
          <div className="kpi-sub">{history.filter(i => i.status !== "payee").length} factures</div>
        </div>
      </div>

      {/* Graphique mensuel */}
      {chartData.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <span className="card-title">📊 Revenus récurrents par mois</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} barGap={2}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={v => `${v.toLocaleString("fr-FR")} €`} />
              <Bar dataKey="total" fill="#1a9e72" radius={[3, 3, 0, 0]} name="Revenus TTC" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tableau historique */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: "var(--border)" }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Historique facturation</span>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input" style={{ width: "auto", marginLeft: 8 }}>
            <option value="all">Tous statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {filtered.length} facture(s) · {fmt(totalRevenue)} €
          </span>
          <button className="btn" style={{ fontSize: 12 }} onClick={exportCSV}>📥 Export CSV</button>
        </div>

        {history.length === 0 ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)", fontSize: 13 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🧾</div>
            Aucune facture d'abonnement générée.<br />
            Utilisez le bouton "Générer facture" dans l'onglet Abonnements.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-secondary, #f8fafc)", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Date</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Client</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Abonnement</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Facture</th>
                <th style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600 }}>Montant TTC</th>
                <th style={{ padding: "9px 14px", textAlign: "left", fontWeight: 600 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => {
                const sc = STATUS_CONFIG[inv.status] || STATUS_CONFIG.en_attente;
                return (
                  <tr key={inv.id} style={{ borderTop: "var(--border)", fontSize: 13 }}>
                    <td style={{ padding: "10px 14px" }}>{fmtDate(inv.billing_date)}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600 }}>{inv.customer_name}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div>{inv.subscription_name}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{inv.frequency}</div>
                    </td>
                    <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>
                      {inv.invoice_number || <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>
                      {fmt(inv.amount_ttc)} €
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 8, fontWeight: 700, background: sc.bg, color: sc.color }}>
                        {sc.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
